
import {finalize} from 'rxjs/operators';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { BaseService } from '../shared/base.service';
import { BaseComponent } from '../shared/base.component';
import { AppService } from '../app.service';
import { NotificationsService } from './notifications.service';
import { NotificationItemModel } from '../shared/notification/notification-item/notification-item.model';
import { PopoverService } from '../shared/popover/popover.service';
import { TYPE_HELP_NOTIFICATION_DOCTOR, TYPE_HELP_NOTIFICATION_ADMIN } from '../shared/constants/help-buttons';
import { DialogComponent } from '../dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-notifications',
    templateUrl: 'notifications.component.html',
    styleUrls: ['notifications.component.scss'],
    providers: [
        NotificationsService,
        PopoverService,
        { provide: 'TYPE_HELP_NOTIFICATION_DOCTOR', useValue: TYPE_HELP_NOTIFICATION_DOCTOR },
        { provide: 'TYPE_HELP_NOTIFICATION_ADMIN', useValue: TYPE_HELP_NOTIFICATION_ADMIN }
    ]
})

export class NotificationsComponent extends BaseComponent implements OnInit {
    @ViewChild('dialog', { static: false }) dialog: DialogComponent;
    selectedNotification: NotificationItemModel;
    notificationParamId: string;
    itemsPerPage = 10;
    currentPage = 1;
    queryParamHelpTemplate = 'TYPE_HELP_NOTIFICATION_DOCTOR';
    helpTemplateName = 'Notification for Doctor Help';

    constructor(public authService: AuthService,
                public router: Router,
                public baseService: BaseService,
                public appService: AppService,
                public popoverService: PopoverService,
                public route: ActivatedRoute,
                public translate: TranslateService
    ) {
        super(authService, router, baseService, appService);

        if (translate) {
            translate.onLangChange.subscribe(() => {
                this.translate.get('(deleted)').subscribe((res: string) => {
                    for (let param in this.selectedNotification.params['deleted_users']) {
                        let key = this.selectedNotification.params['deleted_users'][param];
                        this.selectedNotification.backupParam(key);
                        this.selectedNotification.updateParams(key, this.selectedNotification.params[key + '_original'] + ' <i>' + res + '</i>');
                    }
                });
            });
        }
    }

    ngOnInit() {
        if (!this.appService.notificationLoaded) {
            this.isLoading = true;
        }

        this.appService.fetchUnreadNotification();

        if (this.authService.user.role_id === this.authService.ROLES.CLINIC_ADMIN) {
            this.queryParamHelpTemplate = 'TYPE_HELP_NOTIFICATION_ADMIN';
            this.helpTemplateName = 'Notification for Admin Help';
        }

        this.popoverService.getHelpContent(this.queryParamHelpTemplate).subscribe(data => {
            this.popoverService.setHelpContent(data['helpTemplates']);
        });

        /**
         * 1. Subscribe URL and preload notification data
         * @type {Subscription}
         */
        if(this.route.firstChild){
            this.route.firstChild.params.subscribe(params => {
                this.notificationParamId = params['notificationId'];

                if (this.appService.notifications.length === 0) {
                    return;
                }

                const notification = this.appService.notifications.find((obj) => {
                    return obj.id === this.notificationParamId;
                });

                if (!notification) {
                    return;
                }

                if (!notification.hasRead) {
                    this.appService.markNotificationAsRead(notification.id);
                }
                this.formatNotificationData(notification);
            });
        } else{
            // If no url param notification id => redirect to the id of the first one
            this.notificationParamId = this.appService.notifications[0]['id'];
            this.router.navigate(['/notifications/', this.notificationParamId]).then(() => {
                window.location.reload();
            });
            return;
        }

        /**
         * 2. Update notification when they change (Transfer)
         */
        this.appService.onNotificationChange().subscribe(() => {
            let notification = this.appService.notifications.find((obj) => {
                return obj.id === this.notificationParamId;
            });

            if (!notification) {
                notification = this.appService.notifications[0];
            }

            if (notification) {
                this.formatNotificationData(notification);
            }

            this.isLoading = false;
        });
    }

    get paginationDisplay() {
        return this.appService.notifications.length > this.itemsPerPage;
    }

    /**
     * Display detail of notification
     */
    onClickNotification(selectedNotification: NotificationItemModel) {
        this.router.navigate(['/notifications/', selectedNotification.id]);
    }

    /**
     * Overwrite pagination for notification
     *
     * @param {number} $event
     * @param {boolean} b
     */
    paginateNotification($event: number, b: boolean) {
        this.currentPage = $event;
    }

    private updateNotify(action: string) {
        this.appService.updateNotifications(action).pipe(
            finalize(() => this.isLoading = false))
            .subscribe(response => {
                this.appService.getUnreadNotifications();
            }, error => {
                this.errorMessage = this.appService.parseErrors(error);
            });
    }

    makeReadAll() {
        this.updateNotify('read');
    }

    deleteAll() {
        const dlg = this.dialog.openConfirm({
            title: 'Delete all notifications',
            content: 'Are you sure you want to delete all notifications?',
            icon: 'fas fa-exclamation-triangle'
        });

        dlg.onSubmit(() => {
            this.updateNotify('delete');
            dlg.modal.hide();
        });

        dlg.onCancel(() => {
            return;
        });
    }

    makeUnreadAll() {
        this.updateNotify('unread');
    }

    formatNotificationData(notification) {
        if (this.translate) {
            this.translate.get('(deleted)').subscribe((res: string) => {
                for (let param in notification.params['deleted_users']) {
                    let key = notification.params['deleted_users'][param];
                    notification.backupParam(key);
                    notification.updateParams(key, notification.params[key + '_original'] + ' <i>' + res + '</i>');
                }
                this.selectedNotification = notification;
            });
        } else {
            this.selectedNotification = notification;
        }
    }
}
